/** Toast primitive (Radix). */
import * as React from 'react';
import { createPortal } from 'react-dom';
import * as ToastPrimitives from '@radix-ui/react-toast';
import { cva, type VariantProps } from 'class-variance-authority';
import { AlertTriangle, CheckCircle2, Info, Loader2, X, XCircle } from 'lucide-react';

import { cn } from '@/lib/utils';

/** Radix toast provider that hosts the viewport and live region. */
const ToastProvider = ToastPrimitives.Provider;

/** Viewport portaled to `body`; `hasNavbar` offsets it below the app bar. */
const ToastViewport = React.forwardRef<
  React.ComponentRef<typeof ToastPrimitives.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport> & { hasNavbar?: boolean }
>(({ className, hasNavbar = false, ...props }, ref) => {
  return (
    <ToastPrimitives.Viewport
      ref={ref}
      className={cn(
        'fixed left-1/2 -translate-x-1/2 z-[var(--z-toast)] flex max-h-screen w-full flex-col gap-2 p-4 md:max-w-[420px]',
        hasNavbar ? 'top-16' : 'top-4',
        className,
      )}
      {...props}
    />
  );
});
ToastViewport.displayName = ToastPrimitives.Viewport.displayName;

/** CVA classes for default vs destructive toasts. */
const toastVariants = cva(
  'custom-toast group pointer-events-auto relative flex w-full items-center justify-between space-x-4 overflow-hidden rounded-lg border p-3 pr-8 shadow-2xl data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-y-[var(--radix-toast-swipe-end-y)] data-[swipe=move]:translate-y-[var(--radix-toast-swipe-move-y)] data-[swipe=move]:transition-none',
  {
    variants: {
      variant: {
        default: 'border-[var(--color-1-nuance-3)] bg-[rgb(var(--background-rgb))] text-foreground',
        destructive:
          'destructive group border-destructive bg-destructive text-destructive-foreground',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

/** Single toast root with default or destructive styling. */
const Toast = React.forwardRef<
  React.ComponentRef<typeof ToastPrimitives.Root>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root> & VariantProps<typeof toastVariants>
>(({ className, variant, duration = 3000, ...props }, ref) => {
  return (
    <ToastPrimitives.Root
      ref={ref}
      duration={duration}
      className={cn(toastVariants({ variant }), className)}
      {...props}
    />
  );
});
Toast.displayName = ToastPrimitives.Root.displayName;

/** Optional action button rendered inside a toast. */
const ToastAction = React.forwardRef<
  React.ComponentRef<typeof ToastPrimitives.Action>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Action>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Action
    ref={ref}
    className={cn(
      'inline-flex h-8 shrink-0 items-center justify-center rounded-md border bg-transparent px-3 text-sm font-medium ring-offset-background transition-colors hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 group-[.destructive]:border-muted/40 group-[.destructive]:hover:border-destructive/30 group-[.destructive]:hover:bg-destructive group-[.destructive]:hover:text-destructive-foreground group-[.destructive]:focus:ring-destructive',
      className,
    )}
    {...props}
  />
));
ToastAction.displayName = ToastPrimitives.Action.displayName;

/** Dismiss button in the toast corner. */
const ToastClose = React.forwardRef<
  React.ComponentRef<typeof ToastPrimitives.Close>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Close>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Close
    ref={ref}
    className={cn(
      'absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1.5 text-foreground transition-all duration-200',
      'hover:bg-black/10 dark:hover:bg-white/10 hover:scale-110',
      'focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-2-nuance-1)]/50',
      'group-[.destructive]:text-destructive-foreground',
      'group-[.destructive]:hover:bg-white/20',
      className,
    )}
    toast-close=""
    {...props}
  >
    <X className="h-4 w-4" />
  </ToastPrimitives.Close>
));
ToastClose.displayName = ToastPrimitives.Close.displayName;

/** Accessible toast title (visually hidden). */
const ToastTitle = React.forwardRef<
  React.ComponentRef<typeof ToastPrimitives.Title>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Title>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Title
    ref={ref}
    className={cn('text-sm font-semibold sr-only', className)}
    {...props}
  />
));
ToastTitle.displayName = ToastPrimitives.Title.displayName;

/** Visible toast body text. */
const ToastDescription = React.forwardRef<
  React.ComponentRef<typeof ToastPrimitives.Description>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Description>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Description
    ref={ref}
    className={cn('text-sm opacity-90', className)}
    {...props}
  />
));
ToastDescription.displayName = ToastPrimitives.Description.displayName;

/** Props accepted by the toast root. */
type ToastProps = React.ComponentPropsWithoutRef<typeof Toast>;
/** React element accepted as a toast action. */
type ToastActionElement = React.ReactElement<typeof ToastAction>;

/** Max visible toasts at once. */
const TOAST_LIMIT = 1;
/** Delay before a dismissed toast is removed from memory. */
const TOAST_REMOVE_DELAY = 400;

/** In-flight toast plus optional title, description, and action. */
type ToasterToast = ToastProps & {
  id: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
  action?: ToastActionElement;
};

/** Reducer actions for the in-memory toast list. */
type Action =
  | { type: 'ADD_TOAST'; toast: ToasterToast }
  | { type: 'UPDATE_TOAST'; toast: Partial<ToasterToast> }
  | { type: 'DISMISS_TOAST'; toastId?: ToasterToast['id'] }
  | { type: 'REMOVE_TOAST'; toastId?: ToasterToast['id'] };

/** In-memory toast list (reducer state). */
interface State {
  toasts: ToasterToast[];
}

/** Pending remove timers keyed by toast id. */
const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>();
/** Subscribers notified when in-memory toast state changes. */
const listeners: Array<(state: State) => void> = [];
let memoryState: State = { toasts: [] };
let count = 0;

/** Monotonic toast id (wraps at `MAX_SAFE_INTEGER`). */
function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER;
  return count.toString();
}

/** Apply a reducer action and notify subscribers. */
function dispatch(action: Action) {
  memoryState = reducer(memoryState, action);
  listeners.forEach((listener) => {
    listener(memoryState);
  });
}

/** Schedule `REMOVE_TOAST` after {@link TOAST_REMOVE_DELAY}. */
function addToRemoveQueue(toastId: string) {
  if (toastTimeouts.has(toastId)) {
    return;
  }

  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId);
    dispatch({ type: 'REMOVE_TOAST', toastId });
  }, TOAST_REMOVE_DELAY);

  toastTimeouts.set(toastId, timeout);
}

/** Add, update, dismiss, or remove toasts. */
function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'ADD_TOAST':
      return {
        ...state,
        toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT),
      };
    case 'UPDATE_TOAST':
      return {
        ...state,
        toasts: state.toasts.map((t) => (t.id === action.toast.id ? { ...t, ...action.toast } : t)),
      };
    case 'DISMISS_TOAST': {
      const { toastId } = action;
      if (toastId) {
        addToRemoveQueue(toastId);
      } else {
        state.toasts.forEach((item) => {
          addToRemoveQueue(item.id);
        });
      }
      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === toastId || toastId === undefined ? { ...t, open: false } : t,
        ),
      };
    }
    case 'REMOVE_TOAST':
      if (action.toastId === undefined) {
        return { ...state, toasts: [] };
      }
      return {
        ...state,
        toasts: state.toasts.filter((t) => t.id !== action.toastId),
      };
  }
}

/** Push a toast and return `id` / `dismiss` / `update`. */
function showToast(props: Omit<ToasterToast, 'id'>) {
  const id = genId();
  const update = (next: ToasterToast) => dispatch({ type: 'UPDATE_TOAST', toast: { ...next, id } });
  const dismiss = () => dispatch({ type: 'DISMISS_TOAST', toastId: id });

  dispatch({
    type: 'ADD_TOAST',
    toast: {
      ...props,
      id,
      open: true,
      onOpenChange: (open: boolean) => {
        if (!open) dismiss();
      },
    },
  });

  return { id, dismiss, update };
}

/** Subscribe the host to the in-memory toast list. */
function useToastState() {
  const [state, setState] = React.useState<State>(memoryState);

  React.useEffect(() => {
    listeners.push(setState);
    return () => {
      const index = listeners.indexOf(setState);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    };
  }, []);

  return state;
}

/** Optional title / description / duration for {@link toast} helpers. */
interface ToastOptions {
  title?: string;
  description?: string;
  duration?: number;
}

/** Imperative helpers (`success`, `error`, `warning`, `info`, `loading`). */
export const toast = {
  success: (message: string, options?: ToastOptions) => {
    return showToast({
      description: (
        <div className="flex items-center gap-3">
          <CheckCircle2 className="w-6 h-6 text-[rgb(34,197,94)] flex-shrink-0" />
          <span>{message}</span>
        </div>
      ),
      duration: options?.duration || 3000,
      className: 'border-l-2 border-[rgb(34,197,94)]',
    });
  },

  error: (message: string, options?: ToastOptions) => {
    return showToast({
      description: (
        <div className="flex items-center gap-3">
          <XCircle className="w-6 h-6 text-[rgb(239,68,68)] flex-shrink-0" />
          <span>{message}</span>
        </div>
      ),
      duration: options?.duration || 3000,
      className: 'border-l-2 border-[rgb(239,68,68)]',
    });
  },

  warning: (message: string, options?: ToastOptions) => {
    return showToast({
      description: (
        <div className="flex items-center gap-3">
          <AlertTriangle className="w-6 h-6 text-[rgb(245,158,11)] flex-shrink-0" />
          <span>{message}</span>
        </div>
      ),
      duration: options?.duration || 3000,
      className: 'border-l-2 border-[rgb(245,158,11)]',
    });
  },

  info: (message: string, options?: ToastOptions) => {
    return showToast({
      description: (
        <div className="flex items-center gap-3">
          <Info className="w-6 h-6 text-[var(--color-2-nuance-1)] flex-shrink-0" />
          <span>{message}</span>
        </div>
      ),
      duration: options?.duration || 3000,
      className: 'border-l-2 border-[var(--color-2-nuance-1)]',
    });
  },

  loading: (message: string) => {
    const body = (text: string) => (
      <div className="flex items-center gap-3">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground flex-shrink-0" />
        <span>{text}</span>
      </div>
    );
    const { id, dismiss, update } = showToast({
      description: body(message),
      duration: 999999,
      className: 'border-l-2 border-muted-foreground/30',
    });
    return {
      id,
      dismiss,
      update: (next: string) => update({ id, description: body(next) }),
    };
  },

  default: (message: string, options?: ToastOptions) => {
    return showToast({
      title: options?.title,
      description: message,
      duration: options?.duration || 3000,
    });
  },
};

/** Props for {@link Toaster}. */
interface ToasterProps {
  hasNavbar?: boolean;
}

/** Render one in-memory toast row. */
function renderToast({ id, title, description, action, ...props }: ToasterToast) {
  return (
    <Toast key={id} {...props}>
      <div className="grid gap-1">
        {title ? <ToastTitle className="sr-only">{title}</ToastTitle> : null}
        {description ? <ToastDescription>{description}</ToastDescription> : null}
      </div>
      {action}
      <ToastClose />
    </Toast>
  );
}

/** Host that renders the in-memory toast list above every overlay. */
export function Toaster({ hasNavbar = false }: Readonly<ToasterProps>) {
  const { toasts } = useToastState();

  return createPortal(
    <ToastProvider>
      {toasts.map(renderToast)}
      <ToastViewport hasNavbar={hasNavbar} />
    </ToastProvider>,
    document.body,
  );
}

export {
  type ToastProps,
  type ToastActionElement,
  ToastProvider,
  ToastViewport,
  Toast,
  ToastTitle,
  ToastDescription,
  ToastClose,
  ToastAction,
};
